/**
 * useUserConfig Hook
 * Gestiona la configuración personal del usuario:
 * - Conexión Supabase (URL + Key)
 * - Configuración GitHub (repo seleccionado)
 * - Preferencias de backup (prefijo de rama)
 */

import { useState, useCallback, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const STORAGE_KEY = 'roadmap-user-config-v1';

// Creator account config (pre-configured for the app creator)
const CREATOR_GITHUB_USERNAMES = ['redachaudri-source', 'redachaudri'];
const CREATOR_CONFIG = {
  supabaseUrl: 'https://zapjbtgnmxkhpfykxmnh.supabase.co',
  supabaseKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InphcGpidGdubXhraHBmeWt4bW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY4MDY3NjcsImV4cCI6MjA4MjM4Mjc2N30.boe3ZdBH0Wo_Vmf9Nmhhjh5SnGXq8IG8rRuRlzby1Zg',
  supabaseConnected: true,
  supabaseTablesCreated: true,
  githubOwner: 'redachaudri-source',
  githubRepo: 'tech-service-ecosystem',
  githubRepoUrl: 'https://github.com/redachaudri-source/tech-service-ecosystem',
  backupBranchPrefix: 'fixarr_backup',
  includeDatabase: true,
  createBranch: true,
  downloadZip: true,
  projectName: 'FIXARR',
  projectWebUrl: 'https://roadmap.fixarr.es',
  setupCompleted: true,
  setupStep: 3
};

// Default config structure
const defaultConfig = {
  // Supabase
  supabaseUrl: '',
  supabaseKey: '',
  supabaseConnected: false,
  supabaseTablesCreated: false,

  // GitHub (from OAuth, enhanced with user selection)
  githubRepo: '',
  githubOwner: '',
  githubRepoUrl: '',

  // Backup settings
  backupBranchPrefix: 'backup',
  includeDatabase: true,
  createBranch: true,
  downloadZip: true,

  // Project info
  projectName: '',
  projectWebUrl: '',

  // Setup status
  setupCompleted: false,
  setupStep: 1
};

export const useUserConfig = (user = null) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [userSupabase, setUserSupabase] = useState(null);
  const [isCreator, setIsCreator] = useState(false);

  // Check if user is the creator
  const checkIfCreator = useCallback((authUser) => {
    if (!authUser) return false;
    const githubUsername = authUser?.user_metadata?.user_name || '';
    return CREATOR_GITHUB_USERNAMES.includes(githubUsername.toLowerCase());
  }, []);

  // Load config from localStorage on mount
  useEffect(() => {
    try {
      // Check if this is the creator's account
      const creatorAccount = checkIfCreator(user);
      setIsCreator(creatorAccount);

      if (creatorAccount) {
        // Auto-configure for creator
        setConfig(CREATOR_CONFIG);
        const client = createClient(CREATOR_CONFIG.supabaseUrl, CREATOR_CONFIG.supabaseKey);
        setUserSupabase(client);
        // Save to localStorage so it persists
        localStorage.setItem(STORAGE_KEY, JSON.stringify(CREATOR_CONFIG));
        setLoading(false);
        return;
      }

      // For regular users, load from localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setConfig({ ...defaultConfig, ...parsed });

        // If Supabase was connected, recreate the client
        if (parsed.supabaseUrl && parsed.supabaseKey && parsed.supabaseConnected) {
          const client = createClient(parsed.supabaseUrl, parsed.supabaseKey);
          setUserSupabase(client);
        }
      }
    } catch (error) {
      console.error('Error loading user config:', error);
    } finally {
      setLoading(false);
    }
  }, [user, checkIfCreator]);

  // Save config to localStorage
  const saveConfig = useCallback((newConfig) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
    } catch (error) {
      console.error('Error saving user config:', error);
    }
  }, []);

  // Update a single config value
  const updateConfig = useCallback((key, value) => {
    setConfig(prev => {
      const updated = { ...prev, [key]: value };
      saveConfig(updated);
      return updated;
    });
  }, [saveConfig]);

  // Update multiple config values
  const updateMultiple = useCallback((updates) => {
    setConfig(prev => {
      const updated = { ...prev, ...updates };
      saveConfig(updated);
      return updated;
    });
  }, [saveConfig]);

  /**
   * Test Supabase connection
   */
  const testSupabaseConnection = useCallback(async (url, key) => {
    try {
      const testClient = createClient(url, key);

      // Try a simple query to test connection
      const { error } = await testClient.from('_test_connection_').select('*').limit(1);

      // If error is about table not existing, connection works
      if (error && error.code === '42P01') {
        return { success: true, message: 'Conexión exitosa' };
      }

      // If no error or other error, still connected
      if (!error || error.code !== 'PGRST301') {
        return { success: true, message: 'Conexión exitosa' };
      }

      return { success: false, message: error.message };
    } catch (error) {
      return { success: false, message: error.message || 'Error de conexión' };
    }
  }, []);

  /**
   * Connect to user's Supabase and create tables
   */
  const connectSupabase = useCallback(async (url, key) => {
    try {
      // Validate inputs
      if (!url || !key) {
        return { success: false, error: 'URL y Key son requeridos' };
      }

      if (!url.includes('supabase.co')) {
        return { success: false, error: 'La URL debe ser de Supabase (.supabase.co)' };
      }

      // Test connection first
      const testResult = await testSupabaseConnection(url, key);
      if (!testResult.success) {
        return { success: false, error: `No se pudo conectar: ${testResult.message}` };
      }

      // Create client
      const client = createClient(url, key);
      setUserSupabase(client);

      // Update config
      updateMultiple({
        supabaseUrl: url,
        supabaseKey: key,
        supabaseConnected: true
      });

      return { success: true, message: 'Conectado exitosamente' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [testSupabaseConnection, updateMultiple]);

  /**
   * Create database tables in user's Supabase
   */
  const createTables = useCallback(async () => {
    if (!userSupabase) {
      return { success: false, error: 'No hay conexión a Supabase' };
    }

    try {
      // SQL to create tables
      const createTablesSQL = `
        -- Projects table
        CREATE TABLE IF NOT EXISTS projects (
          id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
          user_id UUID NOT NULL,
          name VARCHAR(255) NOT NULL,
          description TEXT,
          progress INTEGER DEFAULT 0,
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW()
        );

        -- Phases table
        CREATE TABLE IF NOT EXISTS phases (
          id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
          project_id UUID REFERENCES projects(id) ON DELETE CASCADE,
          name VARCHAR(255) NOT NULL,
          order_index INTEGER DEFAULT 0,
          created_at TIMESTAMPTZ DEFAULT NOW()
        );

        -- Tasks table
        CREATE TABLE IF NOT EXISTS tasks (
          id VARCHAR(50) PRIMARY KEY,
          phase_id UUID REFERENCES phases(id) ON DELETE CASCADE,
          name VARCHAR(255) NOT NULL,
          description TEXT,
          status VARCHAR(20) DEFAULT 'pending',
          tech VARCHAR(255),
          time_estimate VARCHAR(100),
          order_index INTEGER DEFAULT 0,
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW()
        );

        -- User settings table
        CREATE TABLE IF NOT EXISTS user_settings (
          id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
          user_id UUID NOT NULL UNIQUE,
          config JSONB DEFAULT '{}',
          created_at TIMESTAMPTZ DEFAULT NOW(),
          updated_at TIMESTAMPTZ DEFAULT NOW()
        );
      `;

      // Execute SQL via RPC or direct query
      const { error } = await userSupabase.rpc('exec_sql', { sql: createTablesSQL });

      if (error) {
        // Try alternative: just check if we can access the tables
        console.log('RPC not available, tables may already exist');
      }

      updateConfig('supabaseTablesCreated', true);
      return { success: true, message: 'Tablas creadas correctamente' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [userSupabase, updateConfig]);

  /**
   * Disconnect Supabase
   */
  const disconnectSupabase = useCallback(() => {
    setUserSupabase(null);
    updateMultiple({
      supabaseUrl: '',
      supabaseKey: '',
      supabaseConnected: false,
      supabaseTablesCreated: false
    });
  }, [updateMultiple]);

  /**
   * Set GitHub repository
   */
  const setGitHubRepo = useCallback((owner, repo) => {
    updateMultiple({
      githubOwner: owner,
      githubRepo: repo,
      githubRepoUrl: `https://github.com/${owner}/${repo}`
    });
  }, [updateMultiple]);

  /**
   * Set backup branch prefix
   */
  const setBackupPrefix = useCallback((prefix) => {
    updateConfig('backupBranchPrefix', prefix);
  }, [updateConfig]);

  /**
   * Mark setup as completed
   */
  const completeSetup = useCallback(() => {
    updateConfig('setupCompleted', true);
  }, [updateConfig]);

  /**
   * Set current setup step
   */
  const setSetupStep = useCallback((step) => {
    updateConfig('setupStep', step);
  }, [updateConfig]);

  /**
   * Reset all config
   */
  const resetConfig = useCallback(() => {
    setConfig(defaultConfig);
    setUserSupabase(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Get backup branch name with prefix
   */
  const getBackupBranchName = useCallback(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${config.backupBranchPrefix}_${day}${month}${year}_${hours}${minutes}`;
  }, [config.backupBranchPrefix]);

  /**
   * Check if minimum setup is done
   */
  const isMinimumSetupDone = useCallback(() => {
    return config.supabaseConnected;
  }, [config.supabaseConnected]);

  /**
   * Check if full setup is done
   */
  const isFullSetupDone = useCallback(() => {
    return config.supabaseConnected && config.githubRepo;
  }, [config.supabaseConnected, config.githubRepo]);

  return {
    config,
    loading,
    userSupabase,
    isCreator,

    // Config updates
    updateConfig,
    updateMultiple,

    // Supabase
    connectSupabase,
    createTables,
    disconnectSupabase,
    testSupabaseConnection,

    // GitHub
    setGitHubRepo,

    // Backup
    setBackupPrefix,
    getBackupBranchName,

    // Setup
    completeSetup,
    setSetupStep,
    resetConfig,

    // Status checks
    isMinimumSetupDone,
    isFullSetupDone
  };
};

export default useUserConfig;
